/** @format */
import React, {
    ForwardRefRenderFunction,
    ReactNode,
    Ref,
    forwardRef,
} from 'react';
import styled from 'styled-components';

import { MediaQueryColumns } from '@toucan/helpers/styles';

import { GridItemContainer } from '@toucan/client/shared/components/grid-repeater/components/grid-item-container';

import { styles } from './grid-repeater.styles';
import {
    calculateColumn,
    calculateRow,
    getMediaMatchedGridColumns,
    isFunctionComponent,
} from './helpers';

export type GridRepeaterProps = {
    children: ReactNode;
    gridColumns?: MediaQueryColumns[] | number;
    gridGap?: string;
    'data-testid'?: string;
    className?: string;
    'data-id'?: string;
};

export type StyledGridRepeaterProps = GridRepeaterProps;

const StyledGridRepeater = styled.div<StyledGridRepeaterProps>`
    ${styles}
`;

const GridRepeaterWithRef: ForwardRefRenderFunction<
    HTMLDivElement,
    GridRepeaterProps
> = (
    {
        children,
        gridColumns = 4,
        gridGap,
        'data-testid': dataTestId = 'grid-repeater',
        'data-id': dataId = 'grid-repeater',
        ...otherProps
    }: GridRepeaterProps,
    ref: Ref<HTMLDivElement>
) => {
    const mediaMatchedGridColumns = getMediaMatchedGridColumns(gridColumns);

    return (
        <StyledGridRepeater
            {...otherProps}
            gridColumns={gridColumns}
            gridGap={gridGap}
            data-testid={dataTestId}
            ref={ref}
            data-id={dataId}
        >
            {children &&
                React.Children.map(children, (child, index: number) => {
                    const row = calculateRow(index, mediaMatchedGridColumns);
                    const column = calculateColumn(
                        index,
                        mediaMatchedGridColumns
                    );

                    return (
                        <GridItemContainer
                            row={row}
                            column={column}
                            key={index}
                            className="grid-item-container"
                            data-testid={`${dataTestId}-item`}
                            data-id={`${dataId}__item-${index}`}
                        >
                            {isFunctionComponent(child)
                                ? React.cloneElement(child)
                                : child}
                        </GridItemContainer>
                    );
                })}
        </StyledGridRepeater>
    );
};

export const GridRepeater = forwardRef(GridRepeaterWithRef);
